import { ItemStatus } from "@focus/focusUI";
import { createSlice, type PayloadAction } from "@reduxjs/toolkit";
import { catchError, filter, map, mergeMap, of } from "rxjs";
import type { User } from "../types/users";
import { createAsyncReducers } from "./helpers";
import { userControllerMessages } from "./userControllerDuck";

type RequestState = {
  status: ItemStatus;
  error: string | null;
};
type RequestsMap = Record<"getUserMe", RequestState>;

interface AuthState {
  authenticated: boolean;
  token?: string;
  username?: string;
  loading: boolean;
  me: User;
  requests: RequestsMap;
}

const initialState: AuthState = {
  authenticated: false,
  token: undefined,
  username: undefined,
  loading: true,
  me: undefined,
  requests: {
    getUserMe: { status: ItemStatus.READY, error: null },
  },
};

const getUserMeAsync = createAsyncReducers<AuthState, "getUserMe", User>(
  "getUserMe",
  (state, action) => {
    state.me = action.payload;
  },
);

export const authSlice = createSlice({
  name: "auth",
  initialState,
  reducers: {
    getUserMeSuccess: getUserMeAsync.success,
    getUserMeFailure: getUserMeAsync.failure,
    setAuthenticated(
      state,
      action: PayloadAction<{ token: string; username: string }>,
    ) {
      state.authenticated = true;
      state.token = action.payload.token;
      state.username = action.payload.username;
      state.loading = false;
    },
    setLoggedOut(state) {
      state.authenticated = false;
      state.token = undefined;
      state.username = undefined;
      state.loading = false;
    },
    setLoading(state, action: PayloadAction<boolean>) {
      state.loading = action.payload;
    },
  },
});

export const {
  setAuthenticated,
  setLoggedOut,
  setLoading,
  getUserMeSuccess,
  getUserMeFailure,
} = authSlice.actions;

const getUserMeEpic = (action, _state, { ajax }) => {
  return action.pipe(
    filter((action: PayloadAction<{ token: string }>) =>
      Boolean(action.payload?.token),
    ),
    mergeMap(() => {
      const url = `/api/v1/access/users/me`;
      return ajax.getJSON(url).pipe(
        map((response: User) => getUserMeSuccess(response)),
        catchError((error) => {
          return of(
            getUserMeFailure(
              error.message || userControllerMessages.user.userInfoError,
            ),
          );
        }),
      );
    }),
  );
};

export default { ...authSlice, middleware: [getUserMeEpic] };
